export function compactKey(value) {
  return String(value || '')
    .toLowerCase()
    .split('?')[0]
    .split('#')[0]
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Returns true when the current session is the System Admin permission group
 * (role_id = 1). Matches the backend's `is_system_admin` bypass so the admin
 * can never be locked out of any page, even if their cached `permissions`
 * array is stale or missing an entry for a freshly added page.
 */
export function isSystemAdminSession() {
  const roleId = Number(localStorage.getItem('role_id') || 0);
  return roleId === 1;
}

function readJSON(key, fallback) {
  try {
    return JSON.parse(localStorage.getItem(key) || JSON.stringify(fallback));
  } catch {
    return fallback;
  }
}

export function getPathCandidates(pathname = '/') {
  const segments = String(pathname || '')
    .split('?')[0]
    .split('#')[0]
    .replace(/^\/+/, '')
    .split('/')
    .filter(Boolean);

  const candidates = [];
  if (segments[0]) candidates.push(segments[0]);
  if (segments[0] && segments[1]) candidates.push(`${segments[0]}/${segments[1]}`);
  if (segments[0] && segments[1]) candidates.push(segments[1]);
  if (segments.length > 2) candidates.push(segments.join('/'));

  const routeAliases = {
    horse: ['horse-nfc'],
  };
  (routeAliases[segments[0]] || []).forEach((alias) => candidates.push(alias));

  return [...new Set(candidates.map(compactKey).filter(Boolean))];
}

export function hasPagePermission(permissions = [], requiredKeys = [], pathname = '/') {
  const have = new Set(
    (permissions || [])
      .filter((permission) => Number(permission?.can_access) === 1)
      .flatMap((permission) => [permission.page_name, permission.permission_key])
      .filter(Boolean)
      .map(compactKey)
  );

  const required = (requiredKeys || []).filter(Boolean).map(compactKey);
  const candidates = required.length ? required : getPathCandidates(pathname);

  if (candidates.some((candidate) => have.has(candidate))) return true;

  return false;
}

/**
 * Action-level permission check for in-page controls (buttons/toolbars) — not
 * route gating. Returns true if the session is System Admin (bypass) or the
 * cached `permissions` array grants the given key (page_name or permission_key).
 * Use for customizable button permissions, e.g. hasActionPermission('horse-manage').
 */
export function hasActionPermission(key) {
  if (!key) return false;
  if (isSystemAdminSession()) return true;
  let perms = [];
  try {
    perms = JSON.parse(localStorage.getItem('permissions') || '[]');
  } catch {
    perms = [];
  }
  return hasPagePermission(perms, [key]);
}

export function isAllowedByDesignedMenu(pathname = '/') {
  const allMenu = readJSON('app_menu_all', []);
  const allowedMenu = readJSON('me_menu', []);

  if (!Array.isArray(allMenu) || allMenu.length === 0) return true;
  if (!Array.isArray(allowedMenu)) return true;

  const candidates = new Set(getPathCandidates(pathname));
  const managed = new Set(
    allMenu
      .filter((item) => Number(item?.is_active) === 1)
      .map((item) => compactKey(item.path || item.route))
      .filter(Boolean)
  );

  const isManagedRoute = [...candidates].some((candidate) => managed.has(candidate));
  if (!isManagedRoute) return true;
  if (allowedMenu.length === 0) return true;

  const allowed = new Set(
    allowedMenu
      .map((item) => compactKey(item.path || item.route))
      .filter(Boolean)
  );

  return [...candidates].some((candidate) => allowed.has(candidate));
}

export function isOwnerRouteAllowed(pathname = '/') {
  const ownerId = localStorage.getItem('owner_id');
  // مسارات «خيولي» لمن يمكن أن يملك خيلًا فقط: مالك (دور 3) أو مدير مربط
  // (is_system_admin) — لا أدوار التشغيل (محاسب/بيطار/بيطري).
  const hasOwnerId = Boolean(ownerId && ownerId !== 'null' && ownerId !== 'undefined');
  const roleId = Number(localStorage.getItem('role_id') || 0);
  const isSystemAdmin = Number(localStorage.getItem('is_system_admin') || 0) === 1;
  const isOwnerAccount = hasOwnerId && (roleId === 3 || isSystemAdmin);

  if (!isOwnerAccount) return false;
  const ownerAllowed = new Set(['myhorses', 'myhorsestats']);
  const isOwnerSelfRoute = getPathCandidates(pathname).some((candidate) => ownerAllowed.has(candidate));
  if (!isOwnerSelfRoute) return false;

  const permissions = readJSON('permissions', []);
  return hasPagePermission(permissions, [], pathname);
}
